import type { IUploadedFile } from '@/apiClient/types/file'

export const DUPLICATE_IN_UPLOAD_LIST_MESSAGE =
  'This file is already in your current upload list.'

/**
 * Deduplicates within a single picker / drop / paste batch.
 * `File` has no path in the browser; name + size + lastModified identifies repeats from one selection.
 */
const dedupeFilesInUploadBatch = (files: File[]) => {
  const seen = new Set<string>()
  const unique: File[] = []
  let duplicateCount = 0

  for (const file of files) {
    const key = `${file.name}\0${file.size}\0${file.lastModified}`
    if (seen.has(key)) {
      duplicateCount += 1
      continue
    }
    seen.add(key)
    unique.push(file)
  }

  return { unique, duplicateCount }
}

const isSameFileAsListItem = (
  file: File,
  item: File | IUploadedFile,
): boolean => {
  if ('id' in item) {
    return item.name === file.name && item.size === file.size
  }
  return (
    item.name === file.name &&
    item.size === file.size &&
    item.lastModified === file.lastModified
  )
}

/**
 * Drops incoming files that are already in `currentList` (pending `File` or uploaded metadata).
 */
const dedupeFilesAgainstCurrentList = (
  files: File[],
  currentList: (File | IUploadedFile)[],
) => {
  const unique: File[] = []
  let duplicateCount = 0

  for (const file of files) {
    if (currentList.some((item) => isSameFileAsListItem(file, item))) {
      duplicateCount++
      continue
    }
    unique.push(file)
  }

  return { unique, duplicateCount }
}

/**
 * Batch-dedupes then removes files that match the current compose list.
 */
export const prepareFilesForUpload = (
  incoming: File[],
  currentList: (File | IUploadedFile)[],
) => {
  const batchDeduped = dedupeFilesInUploadBatch(incoming)
  const listDeduped = dedupeFilesAgainstCurrentList(
    batchDeduped.unique,
    currentList,
  )

  return {
    accepted: listDeduped.unique,
    duplicateCount: batchDeduped.duplicateCount + listDeduped.duplicateCount,
  }
}
